import React, {
	ClipboardEvent, FC, KeyboardEvent, useEffect, useRef, useState,
} from 'react';
import { ColumnCellEditorActions, ICellEditor } from '@aleph/shared-components';
import { XIcon } from '@heroicons/react/solid';
import { AnimatePresence, motion } from 'framer-motion';

const KeywordsCellEditor: FC<ICellEditor<string[] | null>> = (
	params,
) => {
	const { value, setValue } = params;

	const inputRef = useRef<HTMLInputElement>(null);
	const stagedRef = useRef<HTMLDivElement | null>(null);

	const [stagedValue, setStagedValue] = useState('');
	const [search, setSearch] = useState('');

	useEffect(() => {
		if (!stagedValue) {
			inputRef?.current?.focus?.();
		} else {
			stagedRef?.current?.focus?.();
		}
	}, [inputRef, stagedValue]);

	const addStagedValue = () => {
		if (!stagedValue) {
			return;
		}

		setValue([...new Set([...value ?? [], stagedValue])]);
		setStagedValue('');
		setSearch('');
	};

	const confirmStagedValue = () => {
		if (search) {
			setStagedValue(search);
		}
		addStagedValue();
	};

	const onStagedKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Backspace') {
			if (stagedValue) {
				setStagedValue('');
			}
		}

		if (e.key === 'Enter') {
			if (!stagedValue && !search) {
				params.stopEditing();
			}

			addStagedValue();
		}

		if (e.key === 'Escape') {
			params.api.stopEditing(true);
			params.api.setFocusedCell(params.rowIndex, params.column);
		}
	};

	const onPaste = (e: ClipboardEvent) => {
		e.stopPropagation();
		e.preventDefault();

		const pastedTexts = e?.clipboardData?.getData?.('text')?.split?.('\n')?.filter?.(text => !!text) ?? [];

		if (pastedTexts.length > 0) {
			setValue([...new Set([...value ?? [], ...pastedTexts])]);
		}
	};

	return (
		<div
			ref={stagedRef}
			onKeyDown={onStagedKeyDown}
			tabIndex={0}
			className="flex items-center gap-[8px] bg-white h-full w-full outline-none"
		>
			<div className="flex h-full w-full items-center pl-[8px] ">
				<AnimatePresence
					mode="wait"
				>
					{
						stagedValue
						&& <motion.div
							initial={{ opacity: 1, x: 0 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ x: 50, opacity: 0 }}
							transition={{ duration: 0.1 }}
							key="stagedTag"
							className="flex w-full items-center gap-[8px] leading-none outline-none"
						>
							<div className="bg-[#666666] flex flex-1 justify-between p-[4px]
								text-white rounded text-[12px] overflow-hidden"
							>
								<div className='overflow-hidden overflow-ellipsis'>
									{stagedValue}
								</div>

								<div
									className="cursor-pointer"
									onClick={() => setStagedValue('')}
								>
									<XIcon
										height={12}
										width={12}
									/>
								</div>
							</div>
						</motion.div>
					}

					{
						!stagedValue
						&& <div
							className="w-full"
							key="searchTag"
						>
							<input
								ref={inputRef}
								onChange={(e) => setSearch(e.target.value)}
								onPaste={onPaste}
								className="flex-1 overflow-hidden overflow-ellipsis"
								autoFocus
								onKeyDown={(e: KeyboardEvent) => {
									if (e.key === 'Enter' && search) {
										setStagedValue(search);
										inputRef?.current?.focus?.();
									}
								}}

							/>
						</div>
					}
				</AnimatePresence>
			</div>

			<ColumnCellEditorActions
				onCancel={
					stagedValue
						? () => setStagedValue('')
						: () => params.api.stopEditing(true)
				}
				onConfirm={
					stagedValue
						? () => addStagedValue()
						: search
							? () => confirmStagedValue()
							: () => params.api.stopEditing()
				}
				confirmText={stagedValue ? 'ENTER' : 'CONFIRM'}
			/>
		</div>
	);
};

export default KeywordsCellEditor;
